import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { SimulationCueSheetRow } from '@/types/player/simulation-cue-sheet.types';
import type { OutputLayer } from '@/components/preview/output-layer-preview';
import {
  buildInBetweenPlayoutSegmentId,
  isInBetweenPlayoutSegmentKind,
} from '@/helpers/flow-control-in-between-playout.helper';
import { resolveFlowControlExpandSegmentMeta } from '@/helpers/flow-control-expand-segment-meta.helper';
import { collectFlowMediaById, resolveFlowSlotIdFromCueRow } from '@/helpers/simulation-cue-playability.helper';
import {
  FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID,
  FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID,
  FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID,
  FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID,
} from '@/components/flows/detail/slot-list/flow-slot-assets-inline-ids';

export type NextCuePreviewMeta = {
  segmentTitle: string;
  itemTypeLabel: string;
  itemName: string;
  isVideo: boolean;
  /** PowerPoint / presentation decks — static Next preview cannot show animations. */
  isPresentation: boolean;
  videoUrl?: string;
};

function resolveSegmentExpandIdFromCueRow(row: SimulationCueSheetRow): string | null {
  if (isInBetweenPlayoutSegmentKind(row.segmentKind)) {
    const slotId = resolveFlowSlotIdFromCueRow(row);
    return slotId ? buildInBetweenPlayoutSegmentId(row.segmentKind, slotId) : null;
  }
  switch (row.segmentKind) {
    case 'pre_flow':
      return FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID;
    case 'flow_intro':
      return FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID;
    case 'flow_outro':
      return FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID;
    case 'after_flow':
      return FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID;
    case 'session':
    case 'break':
      return resolveFlowSlotIdFromCueRow(row) ?? null;
    default:
      return resolveFlowSlotIdFromCueRow(row) ?? null;
  }
}

function formatFileTypeLabel(
  fileType: string | undefined,
  t: (key: string, options?: { defaultValue?: string }) => string,
): string {
  switch (fileType) {
    case 'video':
      return t('flows.detail.control.nowNext.itemType.video', { defaultValue: 'Video' });
    case 'audio':
      return t('flows.detail.control.nowNext.itemType.audio', { defaultValue: 'Audio' });
    case 'image':
      return t('flows.detail.control.nowNext.itemType.image', { defaultValue: 'Image' });
    case 'pdf':
      return t('flows.detail.control.nowNext.itemType.pdf', { defaultValue: 'PDF' });
    case 'presentation':
      return t('flows.detail.control.nowNext.itemType.presentation', { defaultValue: 'Presentation' });
    case 'imageSequence':
      return t('flows.detail.control.nowNext.itemType.imageSequence', { defaultValue: 'Image sequence' });
    default:
      if (fileType) {
        return fileType;
      }
      return t('flows.detail.control.nowNext.itemType.media', { defaultValue: 'Media' });
  }
}

function resolveVideoUrlFromLayers(layers: OutputLayer[] | undefined): string | undefined {
  for (const layer of layers ?? []) {
    if (layer.componentId !== 'video' && layer.componentId !== 'backgroundVideo') {
      continue;
    }
    const url = layer.data?.videoUrl || layer.data?.url;
    if (url) {
      return url;
    }
  }
  return undefined;
}

function resolveMediaAssetUrl(flow: FlowDto, mediaId: string): string | undefined {
  const media = collectFlowMediaById(flow).get(mediaId);
  const asset = media?.assets?.find((entry) => entry.isApproved) ?? media?.assets?.[0];
  return asset?.uri;
}

/** Operator-facing metadata for the Next cue preview overlay. */
export function resolveNextCuePreviewMeta(params: {
  row: SimulationCueSheetRow;
  flow: FlowDto;
  slots?: FlowSlotDto[];
  layers?: OutputLayer[];
  t: (key: string, options?: { defaultValue?: string }) => string;
}): NextCuePreviewMeta {
  const { row, flow, slots, layers, t } = params;
  const expandId = resolveSegmentExpandIdFromCueRow(row);
  const slot = row.slotId ? slots?.find((entry) => entry.id === row.slotId) : undefined;
  const segmentMeta = expandId ? resolveFlowControlExpandSegmentMeta(expandId, slot, t, slots) : null;
  const segmentTitle =
    segmentMeta?.label ??
    row.label ??
    t('flows.detail.control.nowNext.unknownSegment', { defaultValue: 'Upcoming segment' });

  let itemName = row.label?.trim() || row.itemId;
  let fileType = row.fileType;
  let videoUrl = resolveVideoUrlFromLayers(layers);

  if (row.itemId.startsWith('media:')) {
    const mediaId = row.itemId.slice('media:'.length);
    const media = collectFlowMediaById(flow).get(mediaId);
    if (media?.title) {
      itemName = media.title;
    }
    const asset = media?.assets?.find((entry) => entry.isApproved) ?? media?.assets?.[0];
    if (asset?.fileType) {
      fileType = asset.fileType;
    }
    if (!videoUrl && fileType === 'video') {
      videoUrl = resolveMediaAssetUrl(flow, mediaId);
    }
  } else if (row.itemId.startsWith('composite:') || row.presentationKind === 'composite') {
    fileType = fileType ?? 'composite';
    itemName = row.label?.trim() || row.itemId.slice('composite:'.length) || itemName;
  } else if (row.itemId.startsWith('virtualMedia:')) {
    fileType = fileType ?? 'virtual';
    itemName = row.label?.trim() || row.itemId.slice('virtualMedia:'.length) || itemName;
  }

  const itemTypeLabel =
    fileType === 'composite'
      ? t('flows.detail.control.nowNext.itemType.composite', { defaultValue: 'Info' })
      : fileType === 'virtual'
        ? t('flows.detail.control.nowNext.itemType.virtual', { defaultValue: 'Virtual' })
        : formatFileTypeLabel(fileType, t);

  const hasPresentationLayer = (layers ?? []).some((layer) => layer.componentId === 'presentationSlides');

  return {
    segmentTitle,
    itemTypeLabel,
    itemName,
    isVideo: fileType === 'video',
    isPresentation: fileType === 'presentation' || hasPresentationLayer,
    videoUrl: fileType === 'video' ? videoUrl : undefined,
  };
}

export function formatPreviewDuration(seconds: number | undefined): string | undefined {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) {
    return undefined;
  }
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
